import type { AnyRealtimeRun } from "@trigger.dev/core/v3"
import { computed, onScopeDispose, shallowRef, toValue, watch } from "vue"
import { realtimeSubscriptionManager } from "../core/subscription-manager"
import type {
	MaybeRefOrGetter,
	RealtimeRunsWithTagHookOptions,
	RealtimeRunsWithTagHookResult,
} from "../core/types"

function resolveEnabled(enabled: MaybeRefOrGetter<boolean> | undefined) {
	return enabled === undefined ? true : toValue(enabled)
}

function canSubscribeOnClient() {
	return typeof window !== "undefined"
}

export function useRealtimeRunsWithTag(
	tag: MaybeRefOrGetter<string | undefined>,
	options: RealtimeRunsWithTagHookOptions,
): RealtimeRunsWithTagHookResult {
	const runsById = shallowRef(new Map<string, AnyRealtimeRun>())
	const error = shallowRef<Error | null>(null)
	const subscribed = shallowRef(false)
	let unsubscribe: (() => void) | undefined

	function stopSubscription() {
		unsubscribe?.()
		unsubscribe = undefined
		subscribed.value = false
	}

	watch(
		[
			() => toValue(tag),
			() => toValue(options.accessToken),
			() => toValue(options.baseURL),
			() => resolveEnabled(options.enabled),
		],
		([nextTag, accessToken, baseURL, enabled]) => {
			stopSubscription()
			runsById.value = new Map()
			error.value = null

			if (!canSubscribeOnClient() || !enabled || !nextTag || !accessToken) {
				return
			}

			subscribed.value = true

			unsubscribe = realtimeSubscriptionManager.subscribeToRunsWithTag(
				{
					tag: nextTag,
					accessToken,
					baseURL,
					onError: (subscriptionError) => {
						error.value = subscriptionError
					},
				},
				(run) => {
					error.value = null
					const next = new Map(runsById.value)
					next.set(run.id, run)
					runsById.value = next
				},
			)
		},
		{ immediate: true },
	)

	onScopeDispose(() => {
		stopSubscription()
	})

	const runs = computed(() => [...runsById.value.values()])

	const isLoading = computed(
		() =>
			resolveEnabled(options.enabled) &&
			!!toValue(tag) &&
			!!toValue(options.accessToken) &&
			subscribed.value &&
			runs.value.length === 0 &&
			!error.value,
	)

	return {
		runs,
		error,
		isLoading,
	}
}
